import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, MessageSquare } from "lucide-react";

const empty = { email: "", legal_name: "", display_name: "", role_title: "Member", team: "", employment_type: "fulltime", joining_date: "", probation_end: "", phone: "", personal_email: "", pan: "", aadhaar: "", bank_holder: "", bank_account: "", bank_ifsc: "" };

export default function Employees() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [note, setNote] = useState({ kind: "praise", text: "", amount: "" });

  const load = async () => setRows((await api.get("/employees")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.legal_name || !editing.email) return toast.warning("Email + legal name required");
    if (editing.employee_id) await api.patch(`/employees/${editing.employee_id}`, editing);
    else await api.post("/employees", editing);
    toast.success("Saved"); setEditing(null); load();
  };

  const openDrawer = async (emp) => {
    setDrawer(emp);
    try {
      const r = await api.get(`/timeline/${emp.employee_id}`);
      setTimeline(r.data);
    } catch { setTimeline([]); }
  };

  const addNote = async () => {
    if (!note.text.trim()) return;
    await api.post("/timeline", { employee_id: drawer.employee_id, kind: note.kind, text: note.text, amount: note.amount ? parseFloat(note.amount) : null });
    const r = await api.get(`/timeline/${drawer.employee_id}`);
    setTimeline(r.data); setNote({ kind: "praise", text: "", amount: "" });
    toast.success("Logged");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Employees</h1>
          <p className="text-sm text-[#A1A1AA] mt-1">Full-time, part-time, contractors — one source of truth.</p>
        </div>
        <button onClick={() => setEditing({ ...empty })} className="tt-btn-primary flex items-center gap-2"><Plus size={14} /> New employee</button>
      </div>

      <div className="tt-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F0F0F]">
            <tr className="text-[#71717A] text-xs font-mono">
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Role</th>
              <th className="text-left px-4 py-2">Team</th>
              <th className="text-left px-4 py-2">Joined</th>
              <th className="text-right px-4 py-2">Tenure</th>
              <th className="text-left px-4 py-2 pl-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.employee_id} className="border-t border-[#1E1E1E] hover:bg-[#121212] cursor-pointer" onClick={() => openDrawer(e)}>
                <td className="px-4 py-3">
                  <div className="font-medium">{e.display_name || e.legal_name}</div>
                  <div className="text-xs font-mono text-[#71717A]">{e.email}</div>
                </td>
                <td className="px-4 py-3">{e.role_title}</td>
                <td className="px-4 py-3 text-[#A1A1AA]">{e.team || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{e.joining_date || "—"}</td>
                <td className="px-4 py-3 text-right font-mono text-xs">{e.tenure_days}d</td>
                <td className="px-4 py-3 pl-4">
                  {e.status === "pre_joining" ? <span className="tt-badge-warn">pre-joining</span>
                    : e.status === "exited" ? <span className="tt-badge-neutral">exited</span>
                    : e.ready_at ? <span className="tt-badge-pass">ready</span>
                    : <span className="tt-badge-info">active</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#71717A]">No employees yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-40 p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="w-full max-w-2xl tt-card p-6 my-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4">
              <h2 className="text-lg font-semibold">{editing.employee_id ? "Edit employee" : "New employee"}</h2>
              <button onClick={() => setEditing(null)} className="tt-btn-ghost"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["email","Work email"],["personal_email","Personal email"],["legal_name","Legal name"],["display_name","Display name"],
                ["role_title","Role title"],["team","Team"],["employment_type","Employment type"],["joining_date","Joining date (YYYY-MM-DD)"],
                ["probation_end","Probation end"],["phone","Phone"],["pan","PAN (encrypted)"],["aadhaar","Aadhaar (encrypted)"],
                ["bank_holder","Bank holder"],["bank_account","Bank account (encrypted)"],["bank_ifsc","IFSC"],
              ].map(([k, lbl]) => (
                <input key={k} className="tt-input" placeholder={lbl} value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} />
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="tt-btn-secondary text-sm">Cancel</button>
              <button onClick={save} className="tt-btn-primary text-sm">Save</button>
            </div>
          </div>
        </div>
      )}

      {drawer && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex justify-end z-40" onClick={() => setDrawer(null)}>
          <div className="w-full max-w-[560px] h-full bg-[#0A0A0A] border-l border-[#262626] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#0A0A0A] border-b border-[#1E1E1E] px-5 py-4 flex items-start justify-between">
              <div>
                <div className="text-xl font-semibold">{drawer.display_name || drawer.legal_name}</div>
                <div className="text-xs font-mono text-[#71717A]">{drawer.email}</div>
              </div>
              <button onClick={() => setDrawer(null)} className="tt-btn-ghost"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="tt-card p-4">
                <div className="tt-label mb-2">Profile</div>
                <div className="text-sm space-y-1">
                  <div><span className="text-[#71717A]">Role:</span> {drawer.role_title}</div>
                  <div><span className="text-[#71717A]">Team:</span> {drawer.team || "—"}</div>
                  <div><span className="text-[#71717A]">Joining:</span> <span className="font-mono">{drawer.joining_date || "—"}</span></div>
                  <div><span className="text-[#71717A]">Tenure:</span> <span className="font-mono">{drawer.tenure_days}d</span></div>
                  <div><span className="text-[#71717A]">Probation ends:</span> <span className="font-mono">{drawer.probation_end || "—"}</span></div>
                  {drawer.pan && <div><span className="text-[#71717A]">PAN:</span> <span className="font-mono">{drawer.pan}</span></div>}
                  {drawer.bank_account && <div><span className="text-[#71717A]">Bank:</span> <span className="font-mono">{drawer.bank_holder} · {drawer.bank_account}</span></div>}
                </div>
                <button onClick={() => { setDrawer(null); setEditing(drawer); }} className="tt-btn-secondary text-xs mt-3">Edit</button>
              </div>
              <div className="tt-card p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare size={14} /> <div className="tt-label !mb-0">Timeline</div>
                </div>
                <div className="space-y-2 mb-3 max-h-72 overflow-y-auto">
                  {timeline.map((t) => (
                    <div key={t.entry_id} className="text-sm border-b border-[#1E1E1E] pb-2">
                      <div className="flex items-center gap-2 text-xs font-mono text-[#71717A]">
                        <span className={t.kind === "praise" ? "text-[#22C55E]" : t.kind === "issue" ? "text-[#F5A524]" : t.kind === "admin_note" ? "text-[#71717A]" : "text-[#5B9BFF]"}>{t.kind}</span>
                        <span>·</span><span>{new Date(t.created_at).toLocaleDateString()}</span>
                        {!t.visible_to_employee && <span className="text-[#F5A524]">(internal)</span>}
                      </div>
                      <div>{t.text}</div>
                      {t.amount != null && <div className="font-mono text-xs">₹{t.amount}</div>}
                    </div>
                  ))}
                  {timeline.length === 0 && <div className="text-sm text-[#71717A]">No entries.</div>}
                </div>
                <div className="flex gap-2 mb-2">
                  <select value={note.kind} onChange={(e) => setNote({ ...note, kind: e.target.value })} className="tt-input !w-32 text-xs">
                    <option value="praise">Praise</option>
                    <option value="issue">Issue</option>
                    <option value="bonus">Bonus</option>
                    <option value="appraisal">Appraisal</option>
                    <option value="admin_note">Admin note (internal)</option>
                  </select>
                  {note.kind === "bonus" && <input value={note.amount} onChange={(e) => setNote({ ...note, amount: e.target.value })} placeholder="₹" type="number" className="tt-input !w-24 font-mono text-xs" />}
                </div>
                <textarea rows={2} className="tt-input text-sm" value={note.text} onChange={(e) => setNote({ ...note, text: e.target.value })} placeholder="Entry text…" />
                <button onClick={addNote} className="tt-btn-primary text-xs mt-2">Add entry</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
